import { delay } from "../client.js";
import { listAccounts } from "./accounts.mock.js";

const STORAGE_KEY = "console-design.account-devices.v1";

function nowIso() {
  return new Date().toISOString();
}

function historyId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function fmtLogTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).replace(",", " |");
}

export function formatDeviceTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${date} | ${time}`;
}

function randomDeviceId() {
  return Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16).toUpperCase()).join("");
}

async function accountNameFor(accountId) {
  if (!accountId) return "—";
  const accounts = await listAccounts();
  return accounts.find((a) => a.id === accountId)?.companyName ?? "—";
}

function accountHistoryEntry(action, accountId, accountName, createdAt) {
  return { id: historyId("ah"), action, accountId, accountName, createdAt };
}

function simHistoryEntry(action, sim, previousSim, createdAt) {
  return {
    id: historyId("sh"),
    action,
    simId: sim?.id ?? null,
    simIccid: sim?.iccid ?? "—",
    simType: sim?.type ?? "—",
    previousSimId: previousSim?.id ?? null,
    previousSimIccid: previousSim?.iccid ?? null,
    previousSimType: previousSim?.type ?? null,
    createdAt,
  };
}

function simSnapshot(simId, simType, simNumber) {
  return { id: simId, iccid: simNumber, type: simType };
}

const SEED_LOGS = [
  { id: "log-1", level: "info", message: "Device connected to network", createdAt: "2026-05-29T13:41:03.000Z" },
  { id: "log-2", level: "info", message: "Configuration sync completed", createdAt: "2026-05-29T12:18:44.000Z" },
  { id: "log-3", level: "warn", message: "GPS signal weak — retry scheduled", createdAt: "2026-05-28T09:02:11.000Z" },
];

export const SEED_ACCOUNT_DEVICES = [
  {
    id: "adev-1",
    accountId: "acc-1",
    deviceId: "1A15AFE65DE56DF9",
    deviceTypeId: "dt-2",
    deviceType: "Telematics Unit",
    simId: "sim-1",
    simType: "M2M",
    simNumber: "8991100000000000001",
    values: { topic_prefix: "fleet/acme/unit-1" },
    createdAt: "2026-05-29T13:41:03.000Z",
    updatedAt: "2026-05-29T13:41:03.000Z",
    logs: [...SEED_LOGS],
    accountHistory: [accountHistoryEntry("assigned", "acc-1", "Acme Logistics", "2026-05-29T13:41:03.000Z")],
    simHistory: [simHistoryEntry("bound", simSnapshot("sim-1", "M2M", "8991100000000000001"), null, "2026-05-29T13:41:03.000Z")],
  },
  {
    id: "adev-2",
    accountId: "acc-1",
    deviceId: "1A15AFE65DE55DE3",
    deviceTypeId: "dt-2",
    deviceType: "Telematics Unit",
    simId: "sim-2",
    simType: "Standard",
    simNumber: "8991100000000000002",
    values: { topic_prefix: "fleet/acme/unit-2" },
    createdAt: "2026-05-28T10:22:15.000Z",
    updatedAt: "2026-05-29T11:05:00.000Z",
    logs: [
      { id: "log-4", level: "info", message: "SIM registered successfully", createdAt: "2026-05-28T10:22:15.000Z" },
      { id: "log-5", level: "error", message: "CAN bus timeout on channel 1", createdAt: "2026-05-29T11:05:00.000Z" },
    ],
    accountHistory: [accountHistoryEntry("assigned", "acc-1", "Acme Logistics", "2026-05-28T10:22:15.000Z")],
    simHistory: [
      simHistoryEntry("changed", simSnapshot("sim-2", "Standard", "8991100000000000002"), simSnapshot("sim-1", "M2M", "8991100000000000001"), "2026-05-29T11:05:00.000Z"),
      simHistoryEntry("bound", simSnapshot("sim-1", "M2M", "8991100000000000001"), null, "2026-05-28T10:22:15.000Z"),
    ],
  },
  {
    id: "adev-3",
    accountId: "acc-1",
    deviceId: "1A15AFE65DE54BC1",
    deviceTypeId: "dt-1",
    deviceType: "GPS Tracker",
    simId: "sim-3",
    simType: "M2M",
    simNumber: "8991100000000000003",
    values: { firmware_version: "2.4.1", report_interval: "60" },
    createdAt: "2026-05-20T08:15:00.000Z",
    updatedAt: "2026-05-27T16:40:22.000Z",
    logs: [{ id: "log-6", level: "info", message: "Firmware updated to v2.4.1", createdAt: "2026-05-27T16:40:22.000Z" }],
    accountHistory: [
      accountHistoryEntry("assigned", "acc-1", "Acme Logistics", "2026-05-20T08:15:00.000Z"),
      accountHistoryEntry("deassigned", "acc-2", "Metro Fleet Co", "2026-05-18T12:00:00.000Z"),
      accountHistoryEntry("assigned", "acc-2", "Metro Fleet Co", "2026-05-10T09:00:00.000Z"),
    ],
    simHistory: [simHistoryEntry("bound", simSnapshot("sim-3", "M2M", "8991100000000000003"), null, "2026-05-20T08:15:00.000Z")],
  },
  {
    id: "adev-4",
    accountId: "acc-2",
    deviceId: "2B26BFG76EF67EF0",
    deviceTypeId: "dt-3",
    deviceType: "CAN Gateway",
    simId: "sim-4",
    simType: "Roaming",
    simNumber: "8991100000000000004",
    values: { can_bitrate: "500000" },
    createdAt: "2026-05-15T14:00:00.000Z",
    updatedAt: "2026-05-15T14:00:00.000Z",
    logs: [],
    accountHistory: [accountHistoryEntry("assigned", "acc-2", "Metro Fleet Co", "2026-05-15T14:00:00.000Z")],
    simHistory: [simHistoryEntry("bound", simSnapshot("sim-4", "Roaming", "8991100000000000004"), null, "2026-05-15T14:00:00.000Z")],
  },
  {
    id: "adev-5",
    accountId: null,
    deviceId: "3C37CGH87FG78FG1",
    deviceTypeId: "dt-1",
    deviceType: "GPS Tracker",
    simId: "sim-5",
    simType: "Standard",
    simNumber: "8991100000000000005",
    values: { firmware_version: "1.0.0", report_interval: "120" },
    createdAt: "2026-06-01T10:00:00.000Z",
    updatedAt: "2026-06-01T10:00:00.000Z",
    logs: [{ id: "log-7", level: "info", message: "Device created (unassigned)", createdAt: "2026-06-01T10:00:00.000Z" }],
    accountHistory: [],
    simHistory: [simHistoryEntry("bound", simSnapshot("sim-5", "Standard", "8991100000000000005"), null, "2026-06-01T10:00:00.000Z")],
  },
];

function normalizeDevice(device) {
  return {
    ...device,
    accountHistory: Array.isArray(device.accountHistory) ? device.accountHistory : [],
    simHistory: Array.isArray(device.simHistory) ? device.simHistory : [],
    logs: Array.isArray(device.logs) ? device.logs : [],
  };
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeDevice) : null;
  } catch {
    return null;
  }
}

function writeStore(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items.map(normalizeDevice)));
}

function getAll() {
  return (readStore() ?? SEED_ACCOUNT_DEVICES.map((row) => normalizeDevice({ ...row })));
}

function findDevice(id) {
  const device = getAll().find((d) => d.id === id);
  if (!device) throw new Error("Device not found");
  return device;
}

function prependHistory(list, entry) {
  return [entry, ...(list ?? [])];
}

function applyDeviceUpdate(items, index, payload) {
  const current = items[index];
  const ts = nowIso();
  const nextSimId = payload.simId?.trim() ?? current.simId;
  const nextSimType = payload.simType?.trim() ?? current.simType;
  const nextSimNumber = payload.simNumber?.trim() ?? current.simNumber;

  let simHistory = current.simHistory ?? [];
  if (nextSimId && nextSimId !== current.simId) {
    simHistory = prependHistory(
      simHistory,
      simHistoryEntry(
        current.simId ? "changed" : "bound",
        simSnapshot(nextSimId, nextSimType, nextSimNumber),
        current.simId ? simSnapshot(current.simId, current.simType, current.simNumber) : null,
        ts,
      ),
    );
  }

  const updated = {
    ...current,
    deviceTypeId: payload.deviceTypeId?.trim() ?? current.deviceTypeId,
    deviceType: payload.deviceType?.trim() ?? current.deviceType,
    simId: nextSimId,
    simType: nextSimType,
    simNumber: nextSimNumber,
    values: payload.values ?? current.values ?? {},
    simHistory,
    updatedAt: ts,
  };

  const next = [...items];
  next[index] = updated;
  writeStore(next);
  return updated;
}

export async function listAccountDevices(accountId) {
  await delay(120);
  return getAll().filter((d) => d.accountId === accountId);
}

export async function listUnassignedDevices() {
  await delay(100);
  return getAll().filter((d) => !d.accountId);
}

async function withAccountNames(devices) {
  const accounts = await listAccounts();
  const map = Object.fromEntries(accounts.map((a) => [a.id, a.companyName]));
  return devices.map((d) => ({
    ...d,
    accountName: d.accountId ? (map[d.accountId] ?? "—") : "Unassigned",
  }));
}

export async function listAllDevices() {
  await delay(120);
  return withAccountNames(getAll());
}

export async function updateDeviceById(id, payload) {
  await delay(120);
  const items = getAll();
  const i = items.findIndex((d) => d.id === id);
  if (i < 0) throw new Error("Device not found");
  return applyDeviceUpdate(items, i, payload);
}

export async function deleteDeviceById(id) {
  await delay(100);
  const items = getAll();
  writeStore(items.filter((d) => d.id !== id));
}

export async function deassignDeviceById(id) {
  const device = findDevice(id);
  if (!device.accountId) throw new Error("Device is not assigned to an account");
  return deassignAccountDevice(device.accountId, id);
}

export async function assignDeviceToAccount(deviceId, accountId) {
  await delay(120);
  const items = getAll();
  const i = items.findIndex((d) => d.id === deviceId);
  if (i < 0) throw new Error("Device not found");
  if (items[i].accountId) throw new Error("Device is already assigned to an account");

  const accountName = await accountNameFor(accountId);
  const ts = nowIso();
  const next = [...items];
  next[i] = {
    ...next[i],
    accountId,
    updatedAt: ts,
    accountHistory: prependHistory(next[i].accountHistory, accountHistoryEntry("assigned", accountId, accountName, ts)),
    logs: [{ id: `log-${Date.now()}`, level: "info", message: `Device assigned to ${accountName}`, createdAt: ts }, ...next[i].logs],
  };
  writeStore(next);
  return next[i];
}

export async function createAccountDevice(accountId, payload) {
  await delay(120);
  const items = getAll();
  const ts = nowIso();
  const accountName = accountId ? await accountNameFor(accountId) : null;
  const simId = payload.simId?.trim() || "";
  const simType = payload.simType?.trim() || "";
  const simNumber = payload.simNumber?.trim() || "";

  const device = normalizeDevice({
    id: payload.id ?? `adev-${Date.now()}`,
    accountId: accountId || null,
    deviceId: payload.deviceId?.trim().toUpperCase() || randomDeviceId(),
    deviceTypeId: payload.deviceTypeId?.trim() || "",
    deviceType: payload.deviceType?.trim() || "",
    simId,
    simType,
    simNumber,
    values: payload.values ?? {},
    createdAt: ts,
    updatedAt: ts,
    logs: [{ id: `log-${Date.now()}`, level: "info", message: "Device created", createdAt: ts }],
    accountHistory: accountId ? [accountHistoryEntry("assigned", accountId, accountName, ts)] : [],
    simHistory: simId ? [simHistoryEntry("bound", simSnapshot(simId, simType, simNumber), null, ts)] : [],
  });

  writeStore([device, ...items]);
  return device;
}

export async function updateAccountDevice(accountId, id, payload) {
  await delay(120);
  const items = getAll();
  const i = items.findIndex((d) => d.id === id && d.accountId === accountId);
  if (i < 0) throw new Error("Device not found");
  return applyDeviceUpdate(items, i, payload);
}

export async function deleteAccountDevice(accountId, id) {
  await delay(100);
  const items = getAll();
  writeStore(items.filter((d) => !(d.id === id && d.accountId === accountId)));
}

export async function deassignAccountDevice(accountId, id) {
  await delay(100);
  const items = getAll();
  const i = items.findIndex((d) => d.id === id && d.accountId === accountId);
  if (i < 0) throw new Error("Device not found");

  const accountName = await accountNameFor(accountId);
  const ts = nowIso();
  const next = [...items];
  next[i] = {
    ...next[i],
    accountId: null,
    updatedAt: ts,
    accountHistory: prependHistory(next[i].accountHistory, accountHistoryEntry("deassigned", accountId, accountName, ts)),
    logs: [{ id: `log-${Date.now()}`, level: "info", message: `Device deassigned from ${accountName}`, createdAt: ts }, ...next[i].logs],
  };
  writeStore(next);
  return next[i];
}

export { fmtLogTime };
